import { isEqual, omit } from 'lodash'
import {
    appearanceFieldNames,
    chooseFromGalleryOptionName,
    convertToSlug,
    customizeMailPieceField,
    defaultCustomizationTemplateFieldName,
    fieldsNameWhichAreNotCustomizableMailOptions,
    getCYOFields,
    isPostcardProduct,
    isStreetviewTemplateName,
    makeFieldMultiTouch,
    selectTemplateField,
    streetviewImageFieldName,
    streetviewYesOptionValue,
    templateField,
} from '../../helpers/slug'
import { ShowField } from './ShowField'
import { useEffect, useMemo, useRef, useState } from 'react'

export const MultiTouchFieldsComponent = ({
    item: originalItem,
    parentValue,
    onSelect,
    multiTouchProducts,
    ...props
}) => {
    const mailerKey = originalItem.name

    const { watch, setValue, getValues } = props

    const [defaultCustomizationTemplate, setDefaultCustomizationTemplate] =
        useState({})
    const previousAppearance = useRef(null)
    const [streetviewLocked, setStreetviewLocked] = useState(false)

    const slotIsPostcardProduct = isPostcardProduct({
        slug: convertToSlug(parentValue),
    })

    const fields = useMemo(
        () =>
            getCYOFields(multiTouchProducts, parentValue, mailerKey, {
                streetviewLocked,
            }),
        [multiTouchProducts, parentValue, mailerKey, streetviewLocked],
    )

    const templateFieldName = makeFieldMultiTouch(templateField.name, mailerKey)

    const selectedTemplateFieldKey = makeFieldMultiTouch(
        selectTemplateField.name,
        mailerKey,
    )

    const customizeMailPieceFieldKey = makeFieldMultiTouch(
        customizeMailPieceField.name,
        mailerKey,
    )

    const { templates_and_attributes, product } =
        multiTouchProducts.find(({ name }) => name === parentValue) || {}

    props = omit(props, [
        'item',
        'defaultCustomizationTemplate',
        'enableCustomization',
    ])

    const templateFieldValue = watch(templateFieldName)
    const customizeMailPieceFieldKeyValue = watch(customizeMailPieceFieldKey)
    const selectedTemplateValue = watch(selectedTemplateFieldKey)
    const enableCustomization =
        selectedTemplateValue === chooseFromGalleryOptionName
            ? customizeMailPieceFieldKeyValue
            : Boolean(selectedTemplateValue)

    useEffect(() => {
        const appearanceFields = fields
            .filter((field) => field.type === 'appearance-section')
            .flatMap((field) => field.fields || [])
        const previous = previousAppearance.current
        const productChanged = previous && previous.product !== parentValue
        const template =
            selectedTemplateValue === chooseFromGalleryOptionName
                ? templates_and_attributes?.find(
                      ({ template }) =>
                          template.url === templateFieldValue?.url,
                  )
                : null
        const templateUrl = template?.template?.url
        const resetAppearance =
            productChanged ||
            (templateUrl && previous && previous.templateUrl !== templateUrl)

        previousAppearance.current = { product: parentValue, templateUrl }

        if (productChanged) {
            appearanceFieldNames.forEach((name) => {
                const key = makeFieldMultiTouch(name, mailerKey)
                if (getValues(key) !== undefined) setValue(key, undefined)
            })
        }

        const supportedAppearance = new Set(
            appearanceFields.map((field) => field.name),
        )
        const defaults = Object.fromEntries(
            Object.entries(template?.attributes || {})
                .filter(
                    ([name]) =>
                        !appearanceFieldNames.includes(name) ||
                        supportedAppearance.has(
                            makeFieldMultiTouch(name, mailerKey),
                        ),
                )
                .map(([name, value]) => [
                    makeFieldMultiTouch(name, mailerKey),
                    value,
                ]),
        )

        appearanceFields.forEach((field) => {
            const options = Array.isArray(field.options) ? field.options : []
            const defaultValue = [
                defaults[field.name],
                field.defaultValue,
                field.default,
                options[0],
            ].find((value) => options.includes(value))
            if (defaultValue === undefined) return

            if (template) defaults[field.name] = defaultValue
            const currentValue = getValues(field.name)
            if (
                (resetAppearance || !options.includes(currentValue)) &&
                currentValue !== defaultValue
            ) {
                setValue(field.name, defaultValue)
            }
        })

        setDefaultCustomizationTemplate((current) =>
            isEqual(current, defaults) ? current : defaults,
        )
        const current = getValues(defaultCustomizationTemplateFieldName) || {}
        const nextValue = {
            ...Object.fromEntries(
                Object.entries(current).filter(
                    ([key]) => !key.startsWith(`${mailerKey} `),
                ),
            ),
            ...defaults,
        }
        if (!isEqual(current, nextValue)) {
            setValue(defaultCustomizationTemplateFieldName, nextValue)
        }
    }, [
        fields,
        parentValue,
        mailerKey,
        templateFieldValue,
        selectedTemplateValue,
        templates_and_attributes,
        getValues,
        setValue,
    ])

    useEffect(() => {
        if (!slotIsPostcardProduct) {
            if (streetviewLocked) setStreetviewLocked(false)
            return
        }
        const shouldLock = isStreetviewTemplateName(templateFieldValue?.name)
        if (
            shouldLock &&
            getValues(
                makeFieldMultiTouch(streetviewImageFieldName, mailerKey),
            ) !== streetviewYesOptionValue
        ) {
            setValue(
                makeFieldMultiTouch(streetviewImageFieldName, mailerKey),
                streetviewYesOptionValue,
            )
        }
        setStreetviewLocked(shouldLock)
    }, [
        templateFieldValue,
        slotIsPostcardProduct,
        mailerKey,
        getValues,
        setValue,
        streetviewLocked,
    ])

    if (fields.length < 1) return null

    // we need to remove from fields any field which name is include in fieldsNameWhichAreNotCustomizableMailOptions
    const customzationFields = fields.filter(
        (item) =>
            !fieldsNameWhichAreNotCustomizableMailOptions.some((fieldName) =>
                item.name.includes(fieldName),
            ),
    )

    const shouldRemoveCustomizationField = customzationFields.length === 0

    const filteredFields = shouldRemoveCustomizationField
        ? fields.filter(
              (field) => !field.name.includes(customizeMailPieceField.name),
          )
        : fields

    return filteredFields.map((item) => (
        <ShowField
            onSelect={onSelect}
            key={item.name}
            templates_and_attributes={templates_and_attributes}
            multiTouchProducts={multiTouchProducts}
            defaultCustomizationTemplate={defaultCustomizationTemplate}
            enableCustomization={enableCustomization}
            {...props}
            product={product}
            isFromMultiTouch={true}
            mailerKey={mailerKey}
            item={item}
        />
    ))
}
